import {
  Autocomplete,
  Box,
  createFilterOptions,
  Grid,
  LinearProgress,
  ListItemIcon,
  Menu,
  MenuItem,
  TextField,
  IconButton,
  Checkbox,
  useTheme,
} from "@mui/material";
import { CheckBox, Delete, Edit, MoreVert } from "@mui/icons-material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import InternoModalConfirmacaoSaida from "../../../../../../../components/modalSaindoDaPagina";
import { ModalEditCheckbox } from "../../ModalEditCheckbox";
import useOpcoesEquipamento from "../../hooks/useOpcoesEquipamento";
import useStyles from "./styles";

// Estes três consts eram de módulo em circuito/index.js — grep confirmou que só
// são usados pelos 3 Autocompletes de equipamento (filterOptions e os ícones do
// Checkbox dentro de renderOption), então migraram junto para este arquivo
const filter = createFilterOptions();
const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBox fontSize="small" />;

// Texto de ajuda por grupo — não está no payload de props porque é fixo por
// `tipo`, igual ao label exato já era fixo por grupo no arquivo original
const HELPER_TEXT_BY_TIPO = {
  roteamento:
    "Realizado no PE ou BNG do cliente, conforme a arquitetura definida.",
  entregaFinal:
    "Último equipamento antes da entrega ao cliente, sob responsabilidade da operadora provedora do acesso.",
  entregaFinalCliente:
    "Equipamento instalado na localidade do cliente, responsável pela terminação do serviço.",
};

// Um dos 3 grupos de opções globais de equipamento (roteamento, entrega final ou
// entrega final cliente) do formulário de circuito: Autocomplete com busca/criação
// inline + menu de editar/excluir + modais. Extraído de circuito/index.js (Task 3
// da decomposição) — eram três blocos JSX quase idênticos, um por grupo; aqui um
// único componente parametrizado por `tipo` é usado 3 vezes pelo pai.
export default function OpcoesEquipamento({
  tipo,
  titulo,
  opcoes,
  setOpcoes,
  valorSelecionado,
  onSelecionar,
  editMode,
  loading,
  setLoading,
  setSnackbar,
  styles: sharedStyles,
}) {
  const theme = useTheme();
  const styles = useStyles(theme);

  const {
    aberto,
    setAberto,
    loadingOpcao,
    anchorMenuAcao,
    abrirMenuAcao,
    fecharMenuAcao,
    opcaoEmAcao,
    edicaoAberta,
    abrirEdicao,
    setEdicaoAberta,
    exclusaoAberta,
    abrirExclusao,
    setExclusaoAberta,
    alterarLabelEmEdicao,
    criarOpcao,
    renomearOpcao,
    excluirOpcao,
  } = useOpcoesEquipamento({
    tipo,
    opcoes,
    setOpcoes,
    valorSelecionado,
    onSelecionar,
    setSnackbar,
    setLoading,
  });

  // confirmLoad desabilita o botão Excluir durante o request; os três tipos usam
  // a mesma prop — deleteLoad era ignorada pelo modal e foi corrigida aqui
  const propLoadingConfirmacao = { confirmLoad: loading };

  return (
    <Grid item xs={12} sm={6} md={4} position={"relative"}>
      {!!loadingOpcao && (
        <LinearProgress sx={{ ...sharedStyles?.linearProgress }} />
      )}
      <Autocomplete
        size="small"
        disabled={!editMode || loading}
        isOptionEqualToValue={(option, value) => option?.id === value?.id}
        value={valorSelecionado}
        open={aberto || !!anchorMenuAcao}
        onChange={(event, option) => {
          if (option && option?.inputValue) {
            criarOpcao(option?.inputValue);
          } else {
            onSelecionar(option);
          }

          setAberto(false);
        }}
        filterOptions={(options, params) => {
          const filtered = filter(options, params);

          const { inputValue } = params;
          const isExisting = options?.some(
            (option) =>
              inputValue?.toLowerCase() === option?.label?.toLowerCase(),
          );
          if (inputValue !== "" && !isExisting) {
            filtered.push({
              inputValue,
              label: `Criar "${inputValue}"`,
            });
          }

          return filtered;
        }}
        selectOnFocus
        clearOnBlur
        handleHomeEndKeys
        options={opcoes}
        getOptionLabel={(option) => {
          if (typeof option === "string") {
            return option;
          }
          if (option?.inputValue) {
            return option?.inputValue;
          }
          return option?.label;
        }}
        renderOption={(props, option, { selected }) => {
          const { key, ...optionProps } = props;
          const isAddOption = !!option?.inputValue;

          return (
            <li
              key={key}
              {...optionProps}
              onClick={(event) => {
                event.stopPropagation(); // evita conflito com o onChange interno

                if (!isAddOption) {
                  if (selected) {
                    onSelecionar(null);
                  } else {
                    onSelecionar(option);
                  }
                } else {
                  criarOpcao(option?.inputValue);
                }
              }}
            >
              <Box sx={styles.containerCheckOption}>
                <Box>
                  {!isAddOption && (
                    <Checkbox
                      icon={icon}
                      checkedIcon={checkedIcon}
                      style={{ marginRight: 8 }}
                      checked={selected}
                    />
                  )}
                  {option?.label}
                </Box>

                {!isAddOption && (
                  <IconButton
                    onMouseDown={(e) => {
                      e.preventDefault();
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      abrirMenuAcao(option, e);
                    }}
                  >
                    <MoreVert />
                  </IconButton>
                )}
              </Box>
            </li>
          );
        }}
        renderInput={(params) => (
          <TextField
            sx={styles.autoComplete}
            onClick={() => setAberto(true)}
            {...params}
            label={titulo}
            onBlur={() => setAberto(false)}
            helperText={HELPER_TEXT_BY_TIPO[tipo]}
            FormHelperTextProps={{
              onClick: (e) => {
                e.stopPropagation();
              },
              onMouseDown: (e) => {
                e.stopPropagation();
              },
              sx: {
                cursor: "text",
                userSelect: "text",
                pointerEvents: "auto",
              },
            }}
          />
        )}
      />

      {/* Menu de ações (editar/excluir) da opção clicada pelo botão de 3 pontos */}
      <Menu
        anchorEl={anchorMenuAcao}
        open={!!anchorMenuAcao}
        onClose={fecharMenuAcao}
      >
        <MenuItem onClick={abrirEdicao}>
          <ListItemIcon>
            <Edit fontSize="small" />
          </ListItemIcon>
          Editar
        </MenuItem>
        <MenuItem onClick={abrirExclusao} sx={{ color: "error.main" }}>
          <ListItemIcon sx={{ color: "error.main" }}>
            <Delete fontSize="small" />
          </ListItemIcon>
          Excluir
        </MenuItem>
      </Menu>

      {/* Modal de edição do label da opção */}
      <ModalEditCheckbox
        open={edicaoAberta}
        onClose={() => setEdicaoAberta(false)}
        onSave={renomearOpcao}
        title="Editar opção"
        value={opcaoEmAcao?.label}
        onChange={(e) => alterarLabelEmEdicao(e.target.value)}
      />

      {/* Confirmação de exclusão: opção é global, exclusão afeta todos os clientes */}
      <InternoModalConfirmacaoSaida
        openConfirmacao={exclusaoAberta}
        {...propLoadingConfirmacao}
        setOpenConfirmacao={setExclusaoAberta}
        execute={excluirOpcao}
        title={"Atenção"}
        severity={"error"}
        contentText={
          "Esta ação afetará TODOS os clientes. Se você prosseguir com a operação, o item será deletado permanentemente!"
        }
        confirmText={"Excluir"}
      />
    </Grid>
  );
}
